import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import api from '../../api';

const DocumentStatusTab = ({ activeMenu, showMsg }) => {
  const [submissions, setSubmissions] = useState([]);
  const [pendings, setPendings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeMenu === 'submissions') {
          const res = await api.get('/admin/documents/submissions');
          setSubmissions(res.data || []);
        } else if (activeMenu === 'pendings') {
          const res = await api.get('/admin/documents/pendings');
          setPendings(res.data || []);
        }
      } catch (err) { showMsg("Failed to load status data", "error"); }
      setLoading(false);
    };
    fetchData();
  }, [activeMenu, showMsg]);

  const renderStackedCell = (primary, secondary) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{primary || '--'}</Typography>
      <Typography variant="caption" color="text.secondary">{secondary || '--'}</Typography>
    </Box>
  );

  const renderCenterCell = (content) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{content}</Box>;

  const renderDocCell = (p, docKey) => {
    const isPending = p.row.pending_flags?.[docKey];
    const hasDoc = p.row[docKey];
    let content = <Typography variant="body2" sx={{ color: hasDoc ? 'success.main' : 'text.secondary', fontWeight: hasDoc ? 'bold' : 'normal' }}>{hasDoc ? 'YES' : 'NO'}</Typography>;
    if (isPending) content = <Chip label="YES" size="small" sx={{ bgcolor: '#FEF08A', color: '#854D0E', fontWeight: 'bold' }} />;
    return renderCenterCell(content);
  };

  const sharedCols = [
    { field: 'emp_details', headerName: 'Employee Name & ID', width: 220, valueGetter: (p, r) => `${r.employee_name} ${r.emp_id}`, renderCell: (p) => renderStackedCell(p.row.employee_name, p.row.emp_id) },
    { field: 'dept_details', headerName: 'Department & Designation', width: 220, valueGetter: (p, r) => `${r.department} ${r.designation}`, renderCell: (p) => renderStackedCell(p.row.department, p.row.designation) }
  ];

  const submissionCols = [
    ...sharedCols,
    { field: 'has_aadhar', headerName: 'Aadhaar', width: 90, renderCell: (p) => renderDocCell(p, 'has_aadhar') },
    { field: 'has_pan', headerName: 'PAN', width: 90, renderCell: (p) => renderDocCell(p, 'has_pan') },
    { field: 'has_ela', headerName: 'ELA', width: 90, renderCell: (p) => renderDocCell(p, 'has_ela') },
    { field: 'has_10th', headerName: '10th', width: 90, renderCell: (p) => renderDocCell(p, 'has_10th') },
    { field: 'has_12th', headerName: '12th', width: 90, renderCell: (p) => renderDocCell(p, 'has_12th') },
    { field: 'other_docs', headerName: 'Other Documents', minWidth: 200, flex: 1, renderCell: (p) => renderCenterCell(<Typography variant="body2">{p.row.other_docs_summary || '--'}</Typography>) },
  ];

  const pendingCols = [
    ...sharedCols,
    { field: 'doj', headerName: 'DOJ', width: 110, renderCell: (p) => renderCenterCell(<Typography variant="body2">{p.row.doj}</Typography>) },
    { field: 'experience', headerName: 'Tot Exp & Comp Exp', width: 170, renderCell: (p) => renderStackedCell(p.row.tot_exp, p.row.company_exp) },
    { field: 'education', headerName: 'Edu & Certifications', width: 180, renderCell: (p) => renderStackedCell(p.row.education, p.row.certification) },
    { field: 'missing_docs', headerName: 'Missing Documents', flex: 1, minWidth: 250, renderCell: (p) => renderCenterCell(<Typography variant="body2" color="error.main" sx={{ fontWeight: 500 }}>{p.row.missing_docs}</Typography>) },
  ];

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <DataGrid
        loading={loading}
        rows={activeMenu === 'submissions' ? submissions : pendings}
        columns={activeMenu === 'submissions' ? submissionCols : pendingCols}
        getRowId={(row) => row.emp_id}
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableRowSelectionOnClick
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': { bgcolor: '#f5f5f5', fontWeight: 'bold' },
          '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type': { position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#fff', boxShadow: '2px 0 4px -2px rgba(0,0,0,0.2)' }
        }}
      />
    </Box>
  );
};

export default DocumentStatusTab;